/**
 * <StudentCounters> — ce que l'élève a gagné : ses pétales, son XP, son titre (molecule).
 *
 * Les compteurs se dessinent en glyphes PLEINS du jeu (`<GameGlyph>` : le pétale, l'éclair, la
 * rosette ; décision de Wajih, 15/10) : le système de jeu du livre (chap. 16), où le glyphe pèse
 * autant que le nombre. Jamais l'iconographie fine de l'interface. Deux écritures de la rangée :
 *  - `glyphs` (défaut), celle du header : chaque compteur a son glyphe sur sa pastille, puis sa
 *    valeur un cran au-dessus du texte ;
 *  - `text`, celle du livre sous le salut de l'accueil (« 2 450 XP · 245 pétales ») : l'XP en
 *    gras, le reste en encre secondaire, chaque compteur derrière son glyphe, sans pastille.
 *
 * Une valeur absente ne prend pas de place, et sans aucune valeur la rangée ne se rend pas :
 * retirer la gamification, c'est ne plus la monter. Un seul compteur se rend seul.
 *
 * `trailing` est la place gardée en bout de rangée : là où le livre pose la chip de la série.
 *
 * Elle ne presse pas : dans le header, c'est la pastille de profil qui porte le geste.
 */
import React from "react"
import { View } from "react-native"
import { StyleSheet } from "react-native-unistyles"

import { Row } from "../../atoms/containers/Row"
import { Text } from "../../atoms/typography/Text"
import { formatAmount, formatCount } from "../../utils/gameAmount"
import { GAME_GLYPH_SIZE, GameGlyph, type GameGlyphName } from "../GameGlyph"

type StudentCountersVariant = "glyphs" | "text"

export type StudentCountersProps = {
    /** Les pétales de l'élève. */
    petals?: number
    /** Son XP. */
    xp?: number
    /** Son titre, par exemple « Ninja en herbe ». */
    title?: string | null
    /**
     * `glyphs` (défaut) : le glyphe du jeu sur sa pastille + la valeur. `text` : la ligne du livre,
     * « 2 450 XP · 245 pétales », chaque compteur derrière son glyphe.
     */
    variant?: StudentCountersVariant
    /** En bout de rangée, après les compteurs. Rien par défaut. */
    trailing?: React.ReactNode
    testID?: string
}

/** Le glyphe de la ligne du livre : sans pastille, un peu plus haut que son texte pour peser autant. */
const TEXT_GLYPH_SIZE = 20

export function StudentCounters(props: StudentCountersProps) {
    const { petals, xp, title, variant = "glyphs", trailing, testID } = props

    const hasTitle = title != null && title !== ""
    if (petals == null && xp == null && !hasTitle && trailing == null) return null

    if (variant === "text") {
        // L'ordre du livre : l'XP d'abord, puis les pétales et le titre. Le premier nombre est en
        // gras, le reste en retrait : des pétales seuls sont donc en gras eux aussi.
        const parts = [
            xp != null ? { key: "xp", glyph: "xp" as const, text: formatAmount(xp, "xp"), number: true } : null,
            petals != null
                ? { key: "petals", glyph: "petals" as const, text: formatAmount(petals, "petals"), number: true }
                : null,
            hasTitle ? { key: "title", glyph: "title" as const, text: title, number: false } : null,
        ].filter((part) => part != null)

        return (
            <Row testID={testID} gap="3" style={styles.root}>
                {parts.map((part, index) => {
                    const lead = index === 0 && part.number
                    return (
                        <View key={part.key} style={part.number ? styles.fixed : styles.shrinks}>
                            <Row gap="1">
                                <GameGlyph name={part.glyph} size={TEXT_GLYPH_SIZE} plate={false} />
                                <Text
                                    size="text-md"
                                    weight={lead ? "bold" : "regular"}
                                    color={lead ? "text-primary" : "text-muted"}
                                    numberOfLines={1}
                                    style={styles.value}
                                >
                                    {part.text}
                                </Text>
                            </Row>
                        </View>
                    )
                })}
                {trailing}
            </Row>
        )
    }

    return (
        <Row testID={testID} gap="4" style={styles.root}>
            {petals != null && (
                <Counter
                    glyph="petals"
                    value={formatCount(petals)}
                    accessibilityLabel={formatAmount(petals, "petals")}
                />
            )}
            {xp != null && (
                <Counter glyph="xp" value={formatAmount(xp, "xp")} accessibilityLabel={formatAmount(xp, "xp")} />
            )}
            {hasTitle && <Counter glyph="title" value={title} accessibilityLabel={`Titre : ${title}`} shrinks />}
            {trailing}
        </Row>
    )
}

type CounterProps = {
    glyph: GameGlyphName
    value: string
    accessibilityLabel: string
    /** Le titre est le seul texte long : c'est lui qui cède quand la place manque. */
    shrinks?: boolean
}

/** Un compteur du livre : le glyphe plein sur sa pastille, puis la valeur un cran au-dessus du texte. */
function Counter(props: CounterProps) {
    const { glyph, value, accessibilityLabel, shrinks = false } = props

    // `<Row>` ne porte pas de props d'accessibilité : la vue qui nomme le compteur l'entoure.
    return (
        <View accessible accessibilityLabel={accessibilityLabel} style={shrinks ? styles.shrinks : styles.fixed}>
            <Row gap="2">
                <GameGlyph name={glyph} size={GAME_GLYPH_SIZE.row} />
                <Text size="text-lg" weight="bold" numberOfLines={1} style={styles.value}>
                    {value}
                </Text>
            </Row>
        </View>
    )
}

const styles = StyleSheet.create(() => ({
    /** La rangée rétrécit avec sa fente : le titre se coupe, les nombres jamais. */
    root: { flexShrink: 1, minWidth: 0 },
    shrinks: { flexShrink: 1, minWidth: 0 },
    /** Un nombre et son glyphe gardent leur taille quand le titre cède. */
    fixed: { flexShrink: 0 },
    value: { flexShrink: 1 },
}))
